/**
 * Size chart drawer — Silverback Vest Size Guide (cm) from custom-order,
 * with cm/inches toggle and fit preference sizing on chest (Point 1).
 */

/** @type {Array<{ size: string, a: number, b: number, c: number, d: number, e: number }>} */
const DEFAULT_ROWS = [
  { size: 'XS', a: 100, b: 95, c: 90, d: 38.75, e: 53.75 },
  { size: 'S', a: 105, b: 100, c: 95, d: 40, e: 55 },
  { size: 'M', a: 110, b: 105, c: 100, d: 41.25, e: 56.25 },
  { size: 'L', a: 115, b: 110, c: 105, d: 42.5, e: 57.5 },
  { size: 'XL', a: 120, b: 115, c: 110, d: 43.75, e: 58.75 },
  { size: '2XL', a: 125, b: 120, c: 115, d: 45, e: 60 },
  { size: '3XL', a: 130, b: 125, c: 120, d: 46.25, e: 61.25 },
  { size: '4XL', a: 135, b: 130, c: 125, d: 47.5, e: 62.5 },
];

/**
 * @param {number} cm
 * @returns {number}
 */
function cmToInches(cm) {
  return Math.round((cm / 2.54) * 100) / 100;
}

/**
 * @param {number} value
 * @param {'cm' | 'in'} unit
 * @returns {string}
 */
function formatMeasurement(value, unit) {
  if (unit === 'in') return String(cmToInches(value));
  return String(value);
}

/**
 * @param {number} input
 * @param {'cm' | 'in'} unit
 * @returns {number}
 */
function toCm(input, unit) {
  if (unit === 'in') return input * 2.54;
  return input;
}

/**
 * @param {number} measurementCm
 * @param {'slim' | 'regular' | 'loose'} fit
 * @param {Array<{ size: string, a: number }>} rows
 * @returns {string | null}
 */
function recommendSize(measurementCm, fit, rows) {
  if (!Number.isFinite(measurementCm) || measurementCm <= 0) return null;

  const boundaries = [];
  for (let i = 0; i < rows.length - 1; i += 1) {
    boundaries.push((rows[i].a + rows[i + 1].a) / 2);
  }

  let index = rows.length - 1;
  for (let i = 0; i < boundaries.length; i += 1) {
    if (measurementCm <= boundaries[i]) {
      index = i;
      break;
    }
  }

  if (fit === 'slim' && index > 0) {
    const currentBoundary = boundaries[index - 1];
    if (Math.abs(measurementCm - currentBoundary) <= rows[index].a * 0.04) {
      index -= 1;
    }
  }

  if (fit === 'loose' && index < rows.length - 1) {
    const boundary = boundaries[index];
    if (Math.abs(measurementCm - boundary) <= rows[index].a * 0.04) {
      index += 1;
    }
  }

  return rows[index]?.size || null;
}

/**
 * Move size chart drawers to <body> so fixed positioning isn't trapped
 * inside product-details stacking contexts below the sticky header.
 */
function portalAllSizeChartDrawers() {
  document.querySelectorAll('theme-drawer[id^="zoh-size-chart-"]').forEach((drawer) => {
    if (!(drawer instanceof HTMLElement)) return;
    if (drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
  });
}

/**
 * @param {HTMLElement} root
 */
function initSizeChart(root) {
  if (!(root instanceof HTMLElement) || root.dataset.initialized === 'true') return;
  root.dataset.initialized = 'true';

  portalAllSizeChartDrawers();

  /** @type {Array<{ size: string, a: number, b: number, c: number, d: number, e: number }>} */
  let rows = DEFAULT_ROWS;
  try {
    const dataEl = root.querySelector('[data-zoh-size-chart-data]');
    if (dataEl instanceof HTMLScriptElement && dataEl.textContent) {
      const parsed = JSON.parse(dataEl.textContent);
      if (Array.isArray(parsed.rows) && parsed.rows.length) rows = parsed.rows;
    }
  } catch {
    rows = DEFAULT_ROWS;
  }

  let unit = 'cm';
  let activePanel = 'chart';

  const tabs = root.querySelectorAll('[data-zoh-size-chart-tab]');
  const panels = root.querySelectorAll('[data-zoh-size-chart-panel]');
  const unitButtons = root.querySelectorAll('[data-zoh-size-chart-unit]');
  const tableBody = root.querySelector('[data-zoh-size-chart-table-body]');
  const unitBadge = root.querySelector('[data-zoh-size-chart-input-unit]');
  const input = root.querySelector('[data-zoh-size-chart-input]');
  const fitSelect = root.querySelector('[data-zoh-size-chart-fit]');
  const calculateBtn = root.querySelector('[data-zoh-size-chart-calculate]');
  const result = root.querySelector('[data-zoh-size-chart-result]');
  const resultSize = root.querySelector('[data-zoh-size-chart-result-size]');
  const resultEmpty = root.querySelector('[data-zoh-size-chart-result-empty]');
  const scaleFit = root.querySelector('[data-zoh-size-chart-scale-fit]');
  const scaleMarker = root.querySelector('[data-zoh-size-chart-scale-marker]');

  /**
   * @param {'chart' | 'calculator'} panel
   */
  function setPanel(panel) {
    activePanel = panel;
    tabs.forEach((tab) => {
      if (!(tab instanceof HTMLElement)) return;
      const isActive = tab.dataset.zohSizeChartTab === panel;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
    panels.forEach((panelEl) => {
      if (!(panelEl instanceof HTMLElement)) return;
      const isActive = panelEl.dataset.zohSizeChartPanel === panel;
      panelEl.classList.toggle('is-active', isActive);
      panelEl.hidden = !isActive;
    });
  }

  function renderTable() {
    if (!(tableBody instanceof HTMLElement)) return;
    tableBody.replaceChildren();

    rows.forEach((row) => {
      const tr = document.createElement('tr');
      tr.innerHTML = `
        <th scope="row">${row.size}</th>
        <td>${formatMeasurement(row.a, unit)}</td>
        <td>${formatMeasurement(row.b, unit)}</td>
        <td>${formatMeasurement(row.c, unit)}</td>
        <td>${formatMeasurement(row.d, unit)}</td>
        <td>${formatMeasurement(row.e, unit)}</td>
      `;
      tableBody.appendChild(tr);
    });
  }

  function syncUnits() {
    unitButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      button.setAttribute('aria-pressed', button.dataset.zohSizeChartUnit === unit ? 'true' : 'false');
    });
    if (unitBadge instanceof HTMLElement) {
      unitBadge.textContent = unit;
    }
    renderTable();
  }

  function updateScale(measurementCm) {
    if (!(scaleFit instanceof HTMLElement) || !(scaleMarker instanceof HTMLElement)) return;
    const min = rows[0].a;
    const max = rows[rows.length - 1].a;
    const span = max - min;
    if (span <= 0) return;

    const clamped = Math.max(min, Math.min(max, measurementCm));
    const percent = ((clamped - min) / span) * 100;
    scaleMarker.style.left = `${percent}%`;

    const idx = rows.findIndex((row, i) => {
      const next = rows[i + 1];
      if (!next) return i === rows.length - 1;
      const boundary = (row.a + next.a) / 2;
      return measurementCm <= boundary;
    });

    const current = rows[Math.max(0, idx)];
    const prev = rows[Math.max(0, idx - 1)];
    const next = rows[Math.min(rows.length - 1, idx + 1)];
    const low = prev ? (prev.a + current.a) / 2 : min;
    const high = next ? (current.a + next.a) / 2 : max;
    const left = ((low - min) / span) * 100;
    const width = ((high - low) / span) * 100;
    scaleFit.style.left = `${left}%`;
    scaleFit.style.width = `${width}%`;
  }

  function calculate() {
    if (!(input instanceof HTMLInputElement) || !(fitSelect instanceof HTMLSelectElement)) return;

    const raw = Number.parseFloat(input.value);
    const measurementCm = toCm(raw, unit);
    /** @type {'slim' | 'regular' | 'loose'} */
    const fit = fitSelect.value === 'slim' || fitSelect.value === 'loose' ? fitSelect.value : 'regular';
    const size = recommendSize(measurementCm, fit, rows);

    if (!(result instanceof HTMLElement) || !(resultSize instanceof HTMLElement) || !(resultEmpty instanceof HTMLElement)) {
      return;
    }

    result.hidden = false;

    if (!size) {
      resultSize.hidden = true;
      resultEmpty.hidden = false;
      return;
    }

    resultSize.hidden = false;
    resultEmpty.hidden = true;
    resultSize.textContent = size;
    updateScale(measurementCm);
  }

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      if (!(tab instanceof HTMLElement)) return;
      const panel = tab.dataset.zohSizeChartTab;
      if (panel === 'chart' || panel === 'calculator') setPanel(panel);
    });
  });

  unitButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (!(button instanceof HTMLElement)) return;
      const nextUnit = button.dataset.zohSizeChartUnit;
      if (nextUnit === 'cm' || nextUnit === 'in') {
        unit = nextUnit;
        syncUnits();
      }
    });
  });

  calculateBtn?.addEventListener('click', calculate);

  input?.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      calculate();
    }
  });

  const drawerId = root.dataset.zohSizeChartDrawerId;
  if (drawerId) {
    /** @type {'chart' | 'calculator'} */
    let openPanel = 'chart';

    document.querySelectorAll(`[data-zoh-size-chart-open][aria-controls="${drawerId}"]`).forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const targetPanel = trigger.getAttribute('data-zoh-size-chart-open');
        openPanel = targetPanel === 'calculator' ? 'calculator' : 'chart';
        setPanel(openPanel);
      });
    });

    const drawer = document.getElementById(drawerId);
    drawer?.addEventListener('theme-drawer:open', () => {
      setPanel(openPanel);
    });

    drawer?.addEventListener('theme-drawer:close', () => {
      openPanel = 'chart';
      setPanel('chart');
    });
  }

  syncUnits();
  setPanel('chart');
}

function initAllSizeCharts() {
  portalAllSizeChartDrawers();
  document.querySelectorAll('[data-zoh-size-chart-root]').forEach((root) => {
    initSizeChart(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAllSizeCharts, { once: true });
} else {
  initAllSizeCharts();
}

if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        initAllSizeCharts();
        break;
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export { initSizeChart, recommendSize, DEFAULT_ROWS };
